// lib
import { useState, useRef, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'

import { VIEW_OPTIONS_HELP } from '@aytm/app/stats/page/sidebar/components/lib/helps'

import { convertTraits } from '@aytm/app/stats/lib/query/traits'
import { convertCustomVariables } from '@aytm/app/stats/lib/query/variables'
import { holdScrollAnchor } from '@aytm/app/stats/lib/page/scroll-anchor'
import { heldBackVariables } from '@aytm/app/stats/lib/page/held-back-variables'

// components
import SidebarPopup from '@aytm/app/stats/page/sidebar/components/SidebarPopup'

import ChartModes from '@aytm/app/stats/page/sidebar/view-options/components/ChartModes'
import Demographic from '@aytm/app/stats/page/sidebar/view-options/components/Demographic'
import Translations from '@aytm/app/stats/page/sidebar/view-options/components/Translations'
import Params from '@aytm/app/stats/page/sidebar/view-options/components/Params'

import ArtificialResponses from '@aytm/app/stats/page/sidebar/view-options/components/ArtificialResponses'

import SidebarPopupWarning from '@aytm/app/stats/page/sidebar/components/SidebarPopupWarning'

// styles
import style from '@aytm/app/stats/page/sidebar/view-options/styles/ViewOptionsPanel.module.scss'

const initVariablesOptions = (survey, type, rejected = []) => {
  const _variables = 'customVariables' == type ? convertCustomVariables(survey) || {} : survey[type] || {}

  const variables = Object.keys(_variables).filter(
    key =>
      !rejected.includes(key) &&
      1 < _variables[key].values.length &&
      21 > _variables[key].values.length &&
      !['o', 'a', 'x'].includes(_variables[key].type) &&
      '___' != key.slice(0, 3) &&
      Object.hasOwn(survey.refsList || {}, key.toUpperCase())
  )
  variables.sort()

  return variables
}

const ViewOptionsPanel = ({
  survey,
  model,
  report,
  features,
  onModeChange,
  onParamsChange,
  onGenerateArtificialResponses
}) => {
  const {
    mode,
    trait,
    chartMode,
    translation,
    generatingArtificialResponses,
    isReportVirtual,
    isReportXpert,
    isQAMode
  } = model

  const componentRef = useRef(null)

  const [root, setRoot] = useState(null)

  const setComponentRef = useCallback(node => {
    componentRef.current = node
    setRoot(node?.parentNode ?? null)
  }, [])

  const data = useMemo(() => {
    const data = {
      locale: survey.locale,
      country: survey.country,
      targetingLocales: survey.targetingLocales,
      questions: survey.questions,
      virtual: Object.values(report.current.virtual).filter(
        q => 'dlv' == q.type && 'rb' == q.variant && 1 < q.answerVariants.length && 21 > q.answerVariants.length
      )
    }

    const geoTraits = Array.isArray(survey.traits)
      ? (survey.traits || [])
          .filter(
            t =>
              // a trait carries `families` only when it is geographic, and a throw here leaves the
              // question's answer stuck at loading with nothing on screen to say why
              'trait_var' == t.kind &&
              0 < (t.values?.length ?? 0) &&
              (t.families?.includes('geo') || t.families?.includes('country') || t.families?.includes('region'))
          )
          .map(t => t.id)
          .filter((v, i, a) => i == a.indexOf(v))
      : ['country', 'state', 'county', 'msa', 'region', 'zip']

    const traits = convertTraits(survey).filter(
      t =>
        ((Object.hasOwn(survey.options || {}, 'crosstabTraits') && survey.options.crosstabTraits.includes(t.id)) ||
          (!Object.hasOwn(survey.options || {}, 'crosstabTraits') && !geoTraits.includes(t.id))) &&
        !['o', 'a'].includes(t.type) &&
        t.missing != true &&
        1 < t.values?.length &&
        21 > t.values.length
    )

    data.traits = traits

    const others = heldBackVariables({ features, refsList: survey.refsList, identity: true })

    data.variables = initVariablesOptions(survey, 'variables', others)
    data.pipingVariables = initVariablesOptions(survey, 'pipingVariables')
    data.customVariables = initVariablesOptions(survey, 'customVariables')

    return data
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    survey.locale,
    survey.country,
    survey.targetingLocales,
    survey.options,
    survey.traits,
    survey.questions,
    survey.variables,
    survey.pipingVariables,
    survey.customVariables,
    report.current.virtual,
    features.fielding_health_status.disabled
  ])

  const onChartModeChange = value => {
    if (chartMode == value) return

    const params = { chart_mode: value }
    if ('pie' == value) params.trait = null

    // every question is redrawn in place at a new height, so the reader is held on the one they are
    // reading rather than left at a scroll offset that now belongs to a question above it
    holdScrollAnchor()
    onParamsChange(params, false)
  }

  const onTraitChange = value => {
    if (trait == value) return

    const params = { trait: value }
    if ('pie' == chartMode) params.chart_mode = 'respondents'

    if (null === value) {
      onParamsChange(params, false)
    } else onParamsChange(params)
  }

  const onTranslationChange = value => {
    if (translation == value) return

    const params = { translation: ('*' != value && value) || null }

    onParamsChange(params)
  }

  return (
    <>
      <SidebarPopupWarning
        features={[features['view_options']]}
        isSpecial={'charts' != mode}
        isReportVirtual={isReportVirtual}
        isReportXpert={isReportXpert}
        onModeChange={onModeChange}
      />

      <div ref={setComponentRef} className={style.component}>
        <div className={style['component-title']}>
          View Options
          {root && <SidebarPopup id="view" root={root} help={VIEW_OPTIONS_HELP} width="300px" />}
        </div>

        <ChartModes model={model} features={features} onChartModeChange={onChartModeChange} />
        <Demographic model={model} data={data} features={features} onTraitChange={onTraitChange} />
        <Translations
          root={root}
          model={model}
          data={data}
          features={features}
          onTranslationChange={onTranslationChange}
        />
        <Params survey={survey} model={model} report={report} features={features} onParamsChange={onParamsChange} />

        {features.fake_responses.hidden ? null : (
          <ArtificialResponses
            data={data}
            generatingArtificialResponses={generatingArtificialResponses}
            disabled={isQAMode}
            onGenerateArtificialResponses={onGenerateArtificialResponses}
          />
        )}
      </div>
    </>
  )
}

ViewOptionsPanel.propTypes = {
  survey: PropTypes.object,
  model: PropTypes.object,
  report: PropTypes.object,
  features: PropTypes.object,
  onModeChange: PropTypes.func,
  onParamsChange: PropTypes.func,
  onGenerateArtificialResponses: PropTypes.func
}

ViewOptionsPanel.displayName = 'ViewOptionsPanel'

export default ViewOptionsPanel
